import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Play, 
  Pause, 
  AlertTriangle,
  Settings,
  Zap,
  GitBranch,
  Shield,
  BarChart3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useOperationsMetrics } from '../../hooks/useOperationsMetrics';
import ResiliencePanel from './ResiliencePanel';

const OperationsPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'agents' | 'scheduler' | 'buildstacks' | 'ci' | 'resilience' | 'metrics'>('agents');
  const {
    data,
    isLoading,
    error,
    isControlLoading,
    pauseSystem,
    resumeSystem
  } = useOperationsMetrics();

  const handlePauseResume = async () => {
    if (!data) return;
    
    const result = data.systemStatus.isPaused 
      ? await resumeSystem() 
      : await pauseSystem();
    
    if (!result.success) {
      console.error('Control action failed:', result.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
      case 'active':
      case 'passed':
      case 'completed':
        return 'text-green-400 bg-green-500/20';
      case 'idle':
      case 'queued':
        return 'text-yellow-400 bg-yellow-500/20';
      case 'error':
      case 'failed':
        return 'text-red-400 bg-red-500/20';
      case 'stopped':
      case 'cancelled':
        return 'text-slate-400 bg-slate-500/20';
      default:
        return 'text-slate-400 bg-slate-500/20';
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m ${seconds % 60}s`;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const renderAgentHealthTab = () => (
    <div data-devinid="agent-health-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Agent Health Monitor</h2>
          <p className="text-slate-400 text-sm mt-1">Real-time status of all Beehive agents</p>
        </div>
        <div className="flex items-center gap-3">
          {data?.systemStatus && (
            <div className="flex items-center gap-2 text-sm">
              <div className={`w-2 h-2 rounded-full ${data.systemStatus.isPaused ? 'bg-red-400' : 'bg-green-400'}`} />
              <span className="text-slate-300">
                {data.systemStatus.isPaused ? 'System Paused' : 'System Active'}
              </span>
            </div>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePauseResume}
            disabled={isControlLoading || !data}
            className="flex items-center gap-2"
            data-devinid={data?.systemStatus.isPaused ? "resume-button" : "pause-button"}
          >
            {isControlLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400" />
            ) : data?.systemStatus.isPaused ? (
              <Play className="w-4 h-4" />
            ) : (
              <Pause className="w-4 h-4" />
            )}
            {data?.systemStatus.isPaused ? 'Resume' : 'Pause'}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
        </div>
      ) : error ? (
        <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
          <div className="flex items-center gap-3 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>Error loading agent data: {error}</span>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {data?.agents.map((agent) => (
            <Card key={agent.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4" data-devinid="agent-status">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    <div>
                      <h4 className="font-medium text-white">{agent.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${agent.type === 'fast' ? 'text-blue-400 bg-blue-500/20' : 'text-purple-400 bg-purple-500/20'}`}>
                          {agent.type}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(agent.status)}`}>
                          {agent.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-slate-400">Memory</div>
                    <div className="text-white font-medium">{agent.memoryUsage}MB</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">CPU</div>
                    <div className="text-white font-medium">{agent.cpuUsage}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Tasks</div>
                    <div className="text-white font-medium">{agent.tasksCompleted}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Uptime</div>
                    <div className="text-white font-medium">{agent.uptime}</div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderSchedulerTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Scheduler Status</h2>
        <p className="text-slate-400 text-sm mt-1">Task scheduling and queue management</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
        </div>
      ) : error ? (
        <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
          <div className="flex items-center gap-3 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>Error loading scheduler data: {error}</span>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {data?.scheduler.map((scheduler) => (
            <Card key={scheduler.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Settings className="w-5 h-5 text-blue-400" />
                  <div>
                    <h4 className="font-medium text-white">{scheduler.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(scheduler.status)} mt-1 inline-block`}>
                      {scheduler.status}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-slate-400">Queue</div>
                    <div className="text-white font-medium">{scheduler.queueSize}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Processed</div>
                    <div className="text-white font-medium">{scheduler.processedJobs}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Failed</div>
                    <div className="text-white font-medium">{scheduler.failedJobs}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Avg Time</div>
                    <div className="text-white font-medium">{scheduler.avgProcessingTime}s</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Throughput</div>
                    <div className="text-white font-medium">{scheduler.throughput}/min</div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderBuildStacksTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">BuildStack Runs</h2>
        <p className="text-slate-400 text-sm mt-1">Active and recent build executions</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
        </div>
      ) : error ? (
        <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
          <div className="flex items-center gap-3 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>Error loading buildstack data: {error}</span>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {data?.buildstacks.map((build) => (
            <Card key={build.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Zap className="w-5 h-5 text-blue-400" />
                  <div>
                    <h4 className="font-medium text-white">{build.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(build.status)}`}>
                        {build.status}
                      </span>
                      <span className="text-slate-400 text-xs">
                        {build.completedSteps}/{build.steps} steps
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-slate-400">Duration</div>
                    <div className="text-white font-medium">{formatDuration(build.duration * 60)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Success Rate</div>
                    <div className="text-white font-medium">{build.successRate}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Started</div>
                    <div className="text-white font-medium">{formatTimestamp(build.startTime)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Progress</div>
                    <div className="text-white font-medium">
                      {Math.round((build.completedSteps / build.steps) * 100)}%
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderCITab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">CI/CD Runs</h2>
        <p className="text-slate-400 text-sm mt-1">Continuous integration pipeline status</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
        </div>
      ) : error ? (
        <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
          <div className="flex items-center gap-3 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            <span>Error loading CI data: {error}</span>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {data?.ci.map((pipeline) => (
            <Card key={pipeline.id} className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <GitBranch className="w-5 h-5 text-blue-400" />
                  <div>
                    <h4 className="font-medium text-white">{pipeline.pipeline}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pipeline.status)}`}>
                        {pipeline.status}
                      </span>
                      <span className="text-slate-400 text-xs">
                        {pipeline.branch} • {pipeline.commit}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-6 text-sm">
                  <div className="text-center">
                    <div className="text-slate-400">Duration</div>
                    <div className="text-white font-medium">{formatDuration(pipeline.duration * 60)}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Tests</div>
                    <div className="text-white font-medium">
                      {pipeline.testsPassed}/{pipeline.testsPassed + pipeline.testsFailed}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Coverage</div>
                    <div className="text-white font-medium">{pipeline.coverage}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-slate-400">Started</div>
                    <div className="text-white font-medium">{formatTimestamp(pipeline.startTime)}</div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const [metricsData, setMetricsData] = React.useState<{
    totalRuns: number;
    successes: number;
    failures: number;
    retries: number;
    bees: Record<string, {
      totalRuns: number;
      successes: number;
      failures: number;
      avgLatency: number;
    }>;
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = React.useState(false);
  const [metricsError, setMetricsError] = React.useState<string | null>(null);

  React.useEffect(() => {
      const fetchMetrics = async () => {
        setMetricsLoading(true);
        setMetricsError(null);
        try {
          const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';
          const response = await fetch(`${API_BASE}/api/bees/MetricsBee/run`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Authorization': 'Bearer demo-token-123'
            },
            body: JSON.stringify({ action: 'getMetrics' })
          });
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          const result = await response.json();
          if (result.success && result.data) {
            setMetricsData(result.data);
          } else {
            throw new Error(result.text || 'Failed to fetch metrics');
          }
        } catch (error) {
          console.error('Failed to fetch metrics:', error);
          setMetricsError(error instanceof Error ? error.message : 'Unknown error');
        } finally {
          setMetricsLoading(false);
        }
      };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const calculateErrorRate = (successes: number, failures: number) => {
    const total = successes + failures;
    return total > 0 ? ((failures / total) * 100).toFixed(1) : '0.0';
  };

  const renderMetricsTab = () => {
    return (
      <div className="space-y-6" data-devinid="metrics-panel">
        <div>
          <h2 className="text-lg font-semibold text-white">Bee Metrics Dashboard</h2>
          <p className="text-slate-400 text-sm mt-1">Per-Bee invocation rates, error rates, and latency metrics</p>
        </div>

        {metricsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          </div>
        ) : metricsError ? (
          <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              <span>Error loading metrics: {metricsError}</span>
            </div>
          </Card>
        ) : metricsData ? (
          <div className="space-y-6">
            {/* System Overview */}
            <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
              <div className="flex items-center gap-3 mb-4">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-medium text-white">System Overview</h3>
              </div>
              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-slate-400 text-sm">Total Runs</div>
                  <div className="text-2xl font-bold text-white">{metricsData.totalRuns}</div>
                </div>
                <div className="text-center">
                  <div className="text-slate-400 text-sm">Success Rate</div>
                  <div className="text-2xl font-bold text-green-400">
                    {metricsData.totalRuns > 0 ? ((metricsData.successes / metricsData.totalRuns) * 100).toFixed(1) : '0.0'}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-slate-400 text-sm">Error Rate</div>
                  <div className="text-2xl font-bold text-red-400">
                    {calculateErrorRate(metricsData.successes, metricsData.failures)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-slate-400 text-sm">Retries</div>
                  <div className="text-2xl font-bold text-yellow-400">{metricsData.retries}</div>
                </div>
              </div>
            </Card>

            {/* Per-Bee Metrics */}
            <div className="grid gap-4">
              {Object.entries(metricsData.bees || {}).map(([beeName, beeMetrics]) => (
                <Card key={beeName} className="p-4 bg-slate-800/50 border-slate-700" data-devinid="bee-metrics">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-400" />
                        <div>
                          <h4 className="font-medium text-white">{beeName}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              beeMetrics.failures === 0 ? 'text-green-400 bg-green-500/20' : 
                              calculateErrorRate(beeMetrics.successes, beeMetrics.failures) > '10' ? 'text-red-400 bg-red-500/20' :
                              'text-yellow-400 bg-yellow-500/20'
                            }`}>
                              {beeMetrics.failures === 0 ? 'Healthy' : 
                               calculateErrorRate(beeMetrics.successes, beeMetrics.failures) > '10' ? 'Degraded' : 'Warning'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-5 gap-6 text-sm">
                      <div className="text-center">
                        <div className="text-slate-400">Invocations</div>
                        <div className="text-white font-medium">{beeMetrics.totalRuns}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400">Success Rate</div>
                        <div className="text-green-400 font-medium">
                          {beeMetrics.totalRuns > 0 ? ((beeMetrics.successes / beeMetrics.totalRuns) * 100).toFixed(1) : '0.0'}%
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400">Error Rate</div>
                        <div className="text-red-400 font-medium">
                          {calculateErrorRate(beeMetrics.successes, beeMetrics.failures)}%
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400">Avg Latency</div>
                        <div className="text-white font-medium">{beeMetrics.avgLatency}ms</div>
                      </div>
                      <div className="text-center">
                        <div className="text-slate-400">Failures</div>
                        <div className="text-white font-medium">{beeMetrics.failures}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Prometheus Metrics Link */}
            <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Raw Prometheus Metrics</h3>
                  <p className="text-slate-400 text-sm">Access raw metrics in Prometheus format for external monitoring tools</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const API_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3000';
                    window.open(`${API_BASE}/metrics`, '_blank');
                  }}
                  className="flex items-center gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  View Raw Metrics
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 p-6">
            <div className="text-center text-slate-400">
              No metrics data available
            </div>
          </Card>
        )}
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col h-full p-6 space-y-6"
    >
      <div>
        <h2 className="text-lg font-semibold text-white">Operations Dashboard</h2>
        <p className="text-slate-400">Monitor and control Beehive platform operations</p>
      </div>

      <Card className="p-6 bg-slate-800/50 border-slate-700">
        <div className="flex space-x-1 bg-slate-700/50 p-1 rounded-lg mb-6" data-devinid="internal-nav">
          <button
            onClick={() => setActiveTab('agents')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'agents'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
          >
            <Activity className="w-4 h-4" />
            Agent Health
          </button>
          <button
            onClick={() => setActiveTab('scheduler')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'scheduler'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
          >
            <Settings className="w-4 h-4" />
            Scheduler Status
          </button>
          <button
            onClick={() => setActiveTab('buildstacks')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'buildstacks'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
          >
            <Zap className="w-4 h-4" />
            BuildStack Runs
          </button>
          <button
            onClick={() => setActiveTab('ci')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'ci'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            CI/CD Runs
          </button>
          <button
            onClick={() => setActiveTab('resilience')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'resilience'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
            data-devinid="resilience-tab"
          >
            <Shield className="w-4 h-4" />
            Resilience
          </button>
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'metrics'
                ? 'bg-blue-600 text-white'
                : 'text-slate-400 hover:text-white hover:bg-slate-600'
            }`}
            data-devinid="metrics-tab"
          >
            <BarChart3 className="w-4 h-4" />
            Metrics
          </button>
        </div>

        <div className="min-h-[400px]">
          {activeTab === 'agents' && renderAgentHealthTab()}
          {activeTab === 'scheduler' && renderSchedulerTab()}
          {activeTab === 'buildstacks' && renderBuildStacksTab()}
          {activeTab === 'ci' && renderCITab()}
          {activeTab === 'resilience' && <div data-devinid="resilience-panel"><ResiliencePanel /></div>}
          {activeTab === 'metrics' && renderMetricsTab()}
        </div>
      </Card>
    </motion.div>
  );
};

export default OperationsPanel;
